/**
 * Cart page pickup date scheduler.
 * Populates two native cart attributes (no app needed):
 *   attributes[Pickup Date]
 *   attributes[Pickup Time Window]
 * Requires Flatpickr (loaded via CDN in theme.liquid) to already be on the page.
 */
(function () {
  'use strict';

  /* =====================================================================
   * CONFIG — edit this block to change store hours / booking rules.
   * Nothing below this block should need to change for day/time tweaks.
   * ===================================================================== */
  var PICKUP_CONFIG = {
    // Minimum lead time, in calendar days, before a pickup day can be chosen.
    // Business rule: "2 days after today". If the computed day is closed,
    // the code below simply keeps walking forward to the next open day —
    // that's the "rollover" behavior (e.g. Friday + 2 = Sunday -> rolls to Tuesday).
    minLeadDays: 2,

    // Furthest a customer can book ahead, in calendar days from today.
    maxBookingDays: 7,

    // Per-day availability + display window + optional per-day lead-time override.
    // Keyed by JS day-of-week: 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat.
    //
    // - enabled: whether this weekday can ever be picked.
    // - window: the text shown to the customer + saved to the order once picked.
    // - leadDaysOverride: set a number here to require MORE notice for a specific
    //   day than the global `minLeadDays` above (e.g. a short window might need
    //   extra prep time). Leave as `null` to just use the global default.
    days: {
      0: { name: 'Sunday',    enabled: false, window: null,                   leadDaysOverride: null }, // closed
      1: { name: 'Monday',    enabled: false, window: null,                   leadDaysOverride: null }, // closed
      2: { name: 'Tuesday',   enabled: true,  window: '11:00 AM – 12:30 PM',  leadDaysOverride: null }, // pickup-only day, storefront closed
      3: { name: 'Wednesday', enabled: true,  window: '10:00 AM – 2:00 PM',   leadDaysOverride: null },
      4: { name: 'Thursday',  enabled: true,  window: '10:00 AM – 2:00 PM',   leadDaysOverride: null },
      5: { name: 'Friday',    enabled: true,  window: '10:00 AM – 2:00 PM',   leadDaysOverride: null },
      6: { name: 'Saturday',  enabled: true,  window: '9:00 AM – 11:30 AM',   leadDaysOverride: null }
    }
  };
  /* =====================================================================
   * END CONFIG
   * ===================================================================== */

  function startOfDay(date) {
    var d = new Date(date);
    d.setHours(0, 0, 0, 0);
    return d;
  }

  function addDays(date, days) {
    var d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
  }

  function daysBetween(a, b) {
    var MS_PER_DAY = 24 * 60 * 60 * 1000;
    return Math.round((startOfDay(b) - startOfDay(a)) / MS_PER_DAY);
  }

  function dayConfigFor(date) {
    return PICKUP_CONFIG.days[date.getDay()];
  }

  // Blackout dates come from the shop metafield custom.pickup_blackout_dates
  // (rendered into data-blackout-dates as "YYYY-MM-DD,YYYY-MM-DD").
  var blackoutDates = {};

  function dateKey(date) {
    var m = date.getMonth() + 1;
    var d = date.getDate();
    return date.getFullYear() + '-' + (m < 10 ? '0' : '') + m + '-' + (d < 10 ? '0' : '') + d;
  }

  function isDateDisabled(today, date) {
    if (blackoutDates[dateKey(date)]) return true;

    var cfg = dayConfigFor(date);
    if (!cfg || !cfg.enabled) return true;

    var diff = daysBetween(today, date);
    var leadDays = cfg.leadDaysOverride != null ? cfg.leadDaysOverride : PICKUP_CONFIG.minLeadDays;

    if (diff < leadDays) return true;
    if (diff > PICKUP_CONFIG.maxBookingDays) return true;
    return false;
  }

  function init() {
    var dateInput = document.querySelector('[data-pickup-date-input]');
    if (!dateInput || typeof flatpickr === 'undefined') return;

    var wrapper = dateInput.closest('[data-pickup-date]');
    var rawBlackouts = (wrapper && wrapper.getAttribute('data-blackout-dates')) || '';
    rawBlackouts.split(',').forEach(function (key) {
      key = key.trim();
      if (key) blackoutDates[key] = true;
    });

    var windowInput = document.querySelector('[data-pickup-window-input]');
    var windowDisplay = document.querySelector('[data-pickup-window-display]');
    var errorEl = document.querySelector('[data-pickup-date-error]');
    var cartForm = document.getElementById('cart');

    var today = startOfDay(new Date());
    var maxDate = addDays(today, PICKUP_CONFIG.maxBookingDays);

    function showWindow(date) {
      var cfg = dayConfigFor(date);
      if (!windowInput || !windowDisplay || !cfg) return;
      windowInput.value = cfg.name + ' ' + cfg.window;
      windowDisplay.textContent = 'Pickup window: ' + cfg.name + ', ' + cfg.window;
      windowDisplay.hidden = false;
    }

    function clearWindow() {
      if (windowInput) windowInput.value = '';
      if (windowDisplay) {
        windowDisplay.textContent = '';
        windowDisplay.hidden = true;
      }
    }

    function showError() {
      if (errorEl) errorEl.hidden = false;
      dateInput.setAttribute('aria-invalid', 'true');
    }

    function clearError() {
      if (errorEl) errorEl.hidden = true;
      dateInput.removeAttribute('aria-invalid');
    }

    // If a date was already saved on the cart (e.g. customer came back to the
    // page), restore its time window display on load.
    if (dateInput.value) {
      var parsed = new Date(dateInput.value);
      if (!isNaN(parsed)) showWindow(parsed);
    }

    flatpickr(dateInput, {
      dateFormat: 'l, F j, Y',
      minDate: today,
      maxDate: maxDate,
      disableMobile: true,
      disable: [
        function (date) {
          return isDateDisabled(today, date);
        }
      ],
      onChange: function (selectedDates) {
        if (!selectedDates.length) {
          clearWindow();
          return;
        }
        showWindow(selectedDates[0]);
        clearError();
      }
    });

    if (cartForm) {
      cartForm.addEventListener('submit', function (event) {
        var submitter = event.submitter;
        // Only gate the checkout submission; don't block other submits
        // (e.g. a no-JS "Update" fallback button) that might target this form.
        if (submitter && submitter.name && submitter.name !== 'checkout') return;

        if (!dateInput.value) {
          event.preventDefault();
          showError();
          dateInput.focus();
          dateInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
